"use client";
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Digio?: any;
        ReactNativeWebView?: {
            postMessage: (data: string) => void | undefined;
        };
    }
}

import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { useTransactionFlow } from "@/context/TransactionContext";
import { ACTIONS } from "@/app/components/payment/actions";
import { ButtonSize, RegularButton } from "@/stories/button/button";
import { eSignatureURL, eSignStatus } from "@/app/api/service/digio";
import LoadingIcon from "@/app/assets/icons/loading";
import { statusFailedRZP } from "@/app/api/service/razorpay";
import { getOrderDetailsByISIN } from "@/app/api/service/stocks";
import useMobile from "@/app/hooks/use-mobile";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";
// import { initBranch, trackEvent } from "@/app/utils/branchio";

interface DigioStepProps {
    isAdvisoryFlow?: boolean;
    onComplete: (orderId: string) => void;
    onClose: (value: boolean) => void;
}

interface DigioDocument {
    id: string;
    identifier: string;
    tokenId: string;
}

interface DigioResponse {
    docId: string;
    eSigningUrl: string;
    document: DigioDocument;
}

interface DigioDocumentStatus {
    digio_doc_id: string;
    error_code: string;
    message: string;
    last_state: string | null;
}

interface eSignInStatus {
    orderId: string;
    ISIN: string;
}

const DigioStep: React.FC<DigioStepProps> = ({ onClose, isAdvisoryFlow }) => {
    const { dispatch, state } = useTransactionFlow();
    const { isMobile } = useMobile();
    const { cleverTapTrackEvent } = useCleverTapEvents();

    const [hasDigioSDK, setHasDigioSDK] = useState<boolean>(false);
    const [showDigioModal, setShowDigioModal] = useState<boolean>(false);
    const [digioResponse, setdigioResponse] = useState<DigioResponse>();
    const [isLoading, setisLoading] = useState<boolean>(false);
    const [isESignStatusData, setESignStatusData] = useState<eSignInStatus>();

    let digio: {
        init: () => void;
        submit: (val1: string, val2: string, val3?: string) => void;
    } | null = null;

    const generateEsignURL = async () => {
        const payload = {
            Platform: "web",
            orderId: state.orderId ?? "",
        };

        if (!state.orderId) {
            return false;
        } else {
            return await eSignatureURL({ payload });
        }
    };

    const setDigioScript = (src: string) => {
        if (typeof window !== "undefined") {
            if (typeof window.Digio === "undefined") {
                const script = document.createElement("script");
                script.setAttribute("type", "text/javascript");
                script.setAttribute("id", "digioSdkScript");
                script.setAttribute("src", src);
                document.getElementsByTagName("body")[0].appendChild(script);
                script.addEventListener("load", () => {
                    console.log("Digio SDK script loaded");
                    setHasDigioSDK(true);
                });
            } else {
                setHasDigioSDK(true);
            }
        }
    };

    useEffect(() => {
        const style = document.createElement("style");
        style.setAttribute("id", "digioSdkStyle");
        style.innerHTML = `
            @media (max-width: 470px) {
                [id^=parentdigio-ifm]>div:first-child {
                    z-index: 99999;
                    width: 100% !important;
                    height: 100vh !important;
                    left: auto !important;
                }
            }
        `;
        document.head.appendChild(style);
        return () => {
            style.remove();
        };
    }, []);

    const digioSdkInstallation = () => {
        // digio sdk installaiton
        const orderId = state.orderId;

        if (typeof window !== "undefined" && !digio) {
            const options = {
                environment: process.env.DIGIO_ENV,
                Is_redirection_approach: false,
                is_iframe: true,
                callback: (response: DigioDocumentStatus) => {
                    if (response.hasOwnProperty("error_code") || response?.error_code) {
                        if (isAdvisoryFlow) {
                            window.location.replace(
                                `${process.env.ADVISORY_URL}/orders/${orderId}/payments/status?status=failure`,
                            );
                            return;
                        }

                        dispatch({
                            type: ACTIONS.SET_ERROR,
                            payload: { error: response?.message, orderId: orderId ?? "" },
                        });
                        const payload = {
                            Platform: "web",
                            orderId: state.orderId ?? "",
                        };
                        eSignStatus({ payload }).then((res) => {
                            if (res && res.success && res.data) {
                                statusFailedRZP(state.isin, orderId ?? "").then(() => {
                                    if (isAdvisoryFlow) {
                                        window.location.replace(
                                            `${process.env.ADVISORY_URL}/orders/${orderId}/payments/status?status=failure`,
                                        );

                                        // window.location.href = `${process.env.REACT_APP_ADVISORY_URL}/orders/${orderId}/payments/status?status=failure`;
                                        return;
                                    } else {
                                        return;
                                    }
                                });
                            }
                        });

                        /* razorpayModalDismiss(null, productDetails.ISIN, { "status": "failed" }).then(res => {
                  window.location.reload();
                }); */
                        return;
                    }

                    // setShowEsignReqdModal(false);
                    const payload = {
                        Platform: "web",
                        orderId: state.orderId ?? "",
                    };
                    eSignStatus({ payload }).then((res) => {
                        if (res && res.success && res.data) {
                            setESignStatusData(res.data);
                            // setESignStatusData(res.data);
                            // setPaymentStatus("success");
                            // setModalType("payment")
                            // setStatusMessage("Payment Successful");
                            // setSuccessModal(true);
                        }
                    });
                },
                // logo: "https://www.mylogourl.com/image.jpeg",
                theme: {
                    primaryColor: "#AB3498",
                    secondaryColor: "#000000",
                },
            };
            // setShowEsignReqdModal(false);
            digio = new window.Digio(options);
        }
    };

    const initiateDigioSDK = () => {
        let sdkUrl = "";

        if (typeof window !== "undefined") {
            if (window.Digio && digio && digioResponse) {
                // digio sdk script available.
                console.log("Digio already initiated");
                // setHasDigioSDK(true);
                // digio = digioSdkInstallation();
                digio.init(); // digio sdk already installed.
                digio.submit(digioResponse?.document.id, digioResponse?.document.identifier);
                setdigioResponse(undefined);
            } else {
                // install digio sdk script.
                if (process.env.DIGIO_ENV === "sandbox") {
                    sdkUrl = "https://ext-gateway.digio.in/sdk/v11/digio.js";
                } else if (process.env.DIGIO_ENV === "production") {
                    sdkUrl = "https://app.digio.in/sdk/v11/digio.js";
                }
                setDigioScript(sdkUrl);
            }
        }
    };

    useEffect(() => {
        if (window?.Digio && digioResponse) {
            digioSdkInstallation();
            digio?.init();
            digio?.submit(
                digioResponse?.document.id,
                digioResponse?.document.identifier,
                digioResponse?.document.tokenId,
            );
            setdigioResponse(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasDigioSDK, digioResponse]);

    useEffect(() => {
        initiateDigioSDK();
        return () => {
            if (document.getElementById("digioSdkScript")) {
                document.getElementById("digioSdkScript")?.remove();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isESignStatusData && Object.entries(isESignStatusData).length > 0) {
            const processPayment = async () => {
                try {
                    const orderId = isESignStatusData.orderId;
                    const isin = isESignStatusData.ISIN;

                    if (isin) {
                        const response = await getOrderDetailsByISIN({ isin, orderId });
                        if (
                            response &&
                            response.success &&
                            response.data &&
                            response.data?.status === "created" &&
                            response.data?.digioDocDetails?.agreement_status === "completed"
                        ) {
                            dispatch({
                                type: ACTIONS.SET_PAYMENT_CHOICE,
                                payload: { isin: isin, postDigioTxnData: response.data },
                            });
                        } else if (
                            response?.data?.digioDocDetails?.agreement_status === "requested"
                        ) {
                            // setRetryDigioModal(true);
                        } else if (response?.data?.status !== "created") {
                            if (isAdvisoryFlow) {
                                window.location.replace(
                                    `${process.env.ADVISORY_URL}/orders/${response?.data?.orderId}/payments/status?status=failure`,
                                );
                            } else {
                                dispatch({
                                    type: ACTIONS.SET_ERROR,
                                    payload: { error: response.message, orderId: orderId ?? "" },
                                });
                            }
                        }
                    }
                    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                } catch (error) {
                    if (isAdvisoryFlow) {
                        window.location.replace(
                            `${process.env.ADVISORY_URL}/orders/${state?.orderId}/payments/status?status=failure`,
                        );
                    } else {
                        dispatch({ type: ACTIONS.SET_ERROR, payload: { error: `${error}`, orderId: isESignStatusData?.orderId ?? "" } });
                    }
                }
            };
            processPayment();
        } else {
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isESignStatusData]);

    const handleDigioSubmit = async () => {
        setisLoading(true);

        setAnalyticsEvent("", `US_Transaction_Digiopopup_Proceed`);
        // initBranch()
        //     .then(() => {
        //         trackEvent(`US_Transaction_Digiopopup_Proceed`);
        //     })
        //     .catch((error) => console.error("Error initializing Branch:", error));
        
        cleverTapTrackEvent(`US_Transaction_Digiopopup_Proceed`, {});

        try {
            const result = await generateEsignURL();

            if (result.success) {
                if (window.ReactNativeWebView) {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            data: result.data?.eSigningUrl, // sent digio url for webview.
                        }),
                    );
                    console.log("Webview: ", result.data?.eSigningUrl);
                }
                setdigioResponse(result.data);
                setShowDigioModal(true);
            } else {
                if (isAdvisoryFlow) {
                    window.location.replace(
                        `${process.env.ADVISORY_URL}/orders/${state?.orderId}/payments/status?status=failure`,
                    );
                } else {
                    dispatch({
                        type: ACTIONS.SET_ERROR,
                        payload: { error: `${result.message}`, orderId: state?.orderId ?? "" },
                    });
                }
            }
        } catch (error) {
            if (isAdvisoryFlow) {
                window.location.replace(
                    `${process.env.ADVISORY_URL}/orders/${state?.orderId}/payments/status?status=failure`,
                );
            } else {
                dispatch({
                    type: ACTIONS.SET_ERROR,
                    payload: { error: `${error}`, orderId: state?.orderId ?? "" },
                });
            }
        }
    };

    return (
        !showDigioModal && (
            <Dialog
                open={true}
                onOpenChange={(open: boolean) => {
                    setAnalyticsEvent("", `US_Transaction_Digiopopup_Close`);
                    // initBranch()
                    //     .then(() => {
                    //         trackEvent(`US_Transaction_Digiopopup_Close`);
                    //     })
                    //     .catch((error) => console.error("Error initializing Branch:", error));
                    cleverTapTrackEvent(`US_Transaction_Digiopopup_Close`, {});
                    onClose(open);
                }}
            >
                <DialogContent
                    onPointerDownOutside={(e) => e.preventDefault()}
                    className="w-[90%] !rounded-3xl p-6 md:w-[460px] md:p-9"
                >
                    <VisuallyHidden.Root>
                        <DialogTitle>Your signature is required</DialogTitle>
                    </VisuallyHidden.Root>
                    <div className="w-full">
                        <div className="flex w-full"></div>
                        <div className="mt-5 w-full text-center text-lg font-semibold md:text-xl">
                            Your signature is required
                        </div>
                        <div className="mt-3 text-center font-normal md:text-md md:leading-7">
                            You will now be redirected to Digio in order to sign a few documents and
                            proceed with this purchase.
                        </div>
                    </div>
                    <RegularButton
                        disabled={isLoading}
                        size={isMobile ? ButtonSize.Medium : ButtonSize.Large}
                        className="max-w-full text-lg md:mt-8"
                        label={!isLoading ? "Click to proceed to Digio" : "Redirecting to Digio"}
                        noIcon={!isLoading}
                        icon={<></>}
                        rightIcon={isLoading ? <LoadingIcon className="animate-spin" /> : <> </>}
                        onClick={handleDigioSubmit}
                    />
                </DialogContent>
            </Dialog>
        )
    );
};

export default DigioStep;
//
